"use client";

import { useRouter } from "next/navigation";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AXIS_STYLE, TOOLTIP_STYLE } from "@/lib/recharts-theme";
import { formatRatioPercent } from "@/lib/format";
import type { NicheRanking } from "@/lib/types";

type Props = {
  niches: NicheRanking[];
};

type ChartPoint = {
  name: string;
  score: number;
  category: string;
  marketDemand: number;
  startupCount: number;
  forSaleRatio: number;
};

export function OpportunityChart({ niches }: Props) {
  const router = useRouter();

  const sorted = [...niches]
    .sort((a, b) => b.opportunityScore - a.opportunityScore)
    .slice(0, 12);

  if (sorted.length === 0) {
    return (
      <div className="chart-card">
        <h3 className="card-title">Opportunity score by niche</h3>
        <p className="card-desc">
          Higher score = stronger revenue, better growth, and lighter crowding
        </p>
        <div className="chart-placeholder">No data to display</div>
      </div>
    );
  }

  const maxScore = Math.max(...sorted.map((n) => n.opportunityScore), 1);
  const data: ChartPoint[] = sorted.map((n) => ({
    name: n.label,
    score: n.opportunityScore,
    category: n.category,
    marketDemand: n.marketDemand,
    startupCount: n.startupCount,
    forSaleRatio: n.forSaleRatio,
  }));

  const getBarFill = (_: unknown, index: number) => {
    if (index === 0) return "rgba(245, 158, 11, 0.8)";
    if (index === 1) return "rgba(148, 163, 184, 0.6)";
    if (index === 2) return "rgba(180, 83, 9, 0.6)";
    return "rgba(139, 92, 246, 0.5)";
  };

  return (
    <div className="chart-card">
      <h3 className="card-title">Opportunity score by niche</h3>
      <p className="card-desc">
        Click a bar or category name to open its niche detail page
      </p>
      <div className="chart-container">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data} layout="vertical" margin={{ right: 20, left: 4 }}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} horizontal={false} />
            <XAxis
              type="number"
              domain={[0, maxScore * 1.1]}
              tick={AXIS_STYLE}
              tickFormatter={(v) => String(Math.round(v))}
            />
            <YAxis
              type="category"
              dataKey="name"
              width={120}
              tick={AXIS_STYLE}
              tickLine={false}
            />
            <Tooltip
              {...TOOLTIP_STYLE}
              cursor={{ fill: "rgba(139, 92, 246, 0.1)" }}
              formatter={() => null}
              content={({ active, payload }) => {
                if (!active || !payload?.[0]) return null;
                const p = payload[0].payload as ChartPoint;
                const demandPart = p.marketDemand > 0 ? ` | ${p.marketDemand.toLocaleString()} demand` : "";
                return (
                  <div
                    style={{
                      ...TOOLTIP_STYLE.contentStyle,
                      padding: "12px 14px",
                    }}
                  >
                    <div style={TOOLTIP_STYLE.labelStyle}>
                      {p.score} score | {p.startupCount} startups | {formatRatioPercent(p.forSaleRatio)} for sale
                      {demandPart}
                    </div>
                  </div>
                );
              }}
            />
            <Bar
              dataKey="score"
              radius={[0, 4, 4, 0]}
              maxBarSize={28}
              onClick={(item) => {
                const payload = item?.payload as ChartPoint | undefined;
                if (payload) router.push(`/dashboard/${payload.category}`);
              }}
              cursor="pointer"
            >
              {data.map((_, index) => (
                <Cell key={index} fill={getBarFill(_, index)} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
